import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit2, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const Equipment = ({ user }) => {
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'pool',
    category: '',
    description: '',
    status: 'operational'
  });

  const categories = [
    'Piscina 15m',
    'Piscina 20m',
    'Piscina 25m',
    'Piscina 50m',
    'Riscaldamento',
    'Sistema Sanitario',
    'Altro'
  ];

  useEffect(() => {
    fetchEquipment();
  }, []);

  useEffect(() => {
    // Filter equipment based on category and/or specific equipment selection
    let filtered = equipment;
    
    if (selectedCategory) {
      filtered = filtered.filter(eq => eq.category === selectedCategory);
    }
    
    if (selectedEquipment) {
      filtered = filtered.filter(eq => eq.id === selectedEquipment);
    }
    
    setFilteredEquipment(filtered);
  }, [selectedCategory, selectedEquipment, equipment]);

  const fetchEquipment = async () => {
    try {
      const response = await axiosInstance.get('/equipment');
      setEquipment(response.data);
      setFilteredEquipment(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli impianti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axiosInstance.put(`/equipment/${editingId}`, formData);
        toast.success('Impianto aggiornato!');
      } else {
        await axiosInstance.post('/equipment', formData);
        toast.success('Impianto creato!');
      }
      fetchEquipment();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo impianto?')) return;
    try {
      await axiosInstance.delete(`/equipment/${id}`);
      toast.success('Impianto eliminato!');
      fetchEquipment();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', type: 'pool', category: '', description: '', status: 'operational' });
    setEditingId(null);
  };

  const openEditDialog = (item) => {
    setFormData({
      name: item.name,
      type: item.type,
      category: item.category || '',
      description: item.description || '',
      status: item.status
    });
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const typeColors = {
    pool: 'badge-blue',
    heating: 'badge-red',
    water_system: 'badge-purple',
    other: 'badge-yellow'
  };

  const statusColors = {
    operational: 'badge-green',
    maintenance: 'badge-yellow',
    offline: 'badge-red'
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="equipment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Impianti</h1>
          <p className="text-gray-600">Gestione degli impianti del complesso</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="add-equipment-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Impianto
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="equipment-dialog">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Modifica Impianto' : 'Nuovo Impianto'}</DialogTitle>
                <DialogDescription>
                  Compila i dettagli dell'impianto
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="equipment-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="type">Tipo</Label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="equipment-type-select"
                  >
                    <option value="pool">Piscina</option>
                    <option value="heating">Riscaldamento</option>
                    <option value="water_system">Sistema Idrico</option>
                    <option value="other">Altro</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="category">Categoria</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="equipment-category-select"
                  >
                    <option value="">Seleziona categoria</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="equipment-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="status">Stato</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="equipment-status-select"
                  >
                    <option value="operational">Operativo</option>
                    <option value="maintenance">In Manutenzione</option>
                    <option value="offline">Offline</option>
                  </select>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  data-testid="equipment-submit-button"
                >
                  {editingId ? 'Aggiorna' : 'Crea'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Category and Equipment Filters */}
      <Card className="shadow-md border-0">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Filter */}
            <div>
              <Label htmlFor="categoryFilter" className="text-sm font-medium mb-2 block">
                Filtra per Categoria:
              </Label>
              <select
                id="categoryFilter"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedEquipment('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="category-filter-select"
              >
                <option value="">Tutte le categorie ({equipment.length} impianti)</option>
                {categories.map((category) => {
                  const count = equipment.filter(eq => eq.category === category).length;
                  return (
                    <option key={category} value={category}>
                      {category} ({count} impianti)
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Equipment Filter */}
            <div>
              <Label htmlFor="equipmentFilter" className="text-sm font-medium mb-2 block">
                {selectedCategory ? 'Filtra per Impianto Specifico:' : 'Filtra per Impianto:'}
              </Label>
              <select
                id="equipmentFilter"
                value={selectedEquipment}
                onChange={(e) => setSelectedEquipment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="equipment-filter-select"
              >
                <option value="">
                  {selectedCategory ? `Tutti gli impianti in ${selectedCategory}` : 'Tutti gli impianti'}
                </option>
                {equipment
                  .filter(item => !selectedCategory || item.category === selectedCategory)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {(selectedCategory || selectedEquipment) && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedCategory('');
                  setSelectedEquipment('');
                }}
                data-testid="clear-filters-button"
              >
                Pulisci filtri
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEquipment.length === 0 ? (
          <Card className="shadow-md border-0 col-span-full">
            <CardContent className="pt-6 text-center text-gray-500">
              {selectedCategory || selectedEquipment
                ? 'Nessun impianto trovato con i filtri selezionati'
                : 'Nessun impianto registrato'}
            </CardContent>
          </Card>
        ) : (
          filteredEquipment.map((item) => (
          <Card key={item.id} className="card-hover shadow-lg border-0" data-testid={`equipment-card-${item.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 mr-3">
                    <Wrench className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{item.name}</CardTitle>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {item.category && (
                        <span className="badge badge-purple text-xs">{item.category}</span>
                      )}
                      <span className={`badge ${typeColors[item.type]}`}>{item.type}</span>
                      <span className={`badge ${statusColors[item.status]}`}>{item.status}</span>
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditDialog(item)}
                      className="p-1.5 rounded-lg hover:bg-gray-100"
                      data-testid={`edit-equipment-${item.id}`}
                    >
                      <Edit2 className="w-4 h-4 text-gray-600" />
                    </button>
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50"
                        data-testid={`delete-equipment-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {item.description && (
                <p className="text-sm text-gray-600">{item.description}</p>
              )}
            </CardContent>
          </Card>
        ))
        )}
      </div>
    </div>
  );
};

export default Equipment;